import { useReducer } from 'react';
import { FormField, Select, TextInput } from 'grommet';
import { HelpWith } from '@components/basicComponents';
import { HelpWithType } from '@customTypes/enums';
import type { FormFieldsProps } from '@customTypes/types';
import type { PrivateCloudSupportFormFieldsState } from '@customTypes/stateTypes';
import {
  helpWithOptions,
  businessOptions,
  enterpriseOptions,
  helpWithThirdOptions,
  severityOptions,
} from './PrivateCloudSupportFormOptions';

export const PrivateCloudSupportFormFields = (props: FormFieldsProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: PrivateCloudSupportFormFieldsState, newState: Partial<PrivateCloudSupportFormFieldsState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      helpWithSecondary: '',
      helpWithThird: '',
      secondDropdownOptions: null,
      thirdDropdownOptions: [],
      isSerialNumberShown: false,
    }
  );

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }
    clearSecondSelectState();

    switch (option) {
      case helpWithOptions.ai:
        updateState({ secondDropdownOptions: null });
        props.onDetailsChange({ showDetails: true });
        break;
      case helpWithOptions.business:
        updateState({ secondDropdownOptions: businessOptions });
        break;
      case helpWithOptions.enterprise:
        updateState({ secondDropdownOptions: enterpriseOptions });
        break;
    }

    updateState({ helpWith: option, helpWithSecondary: '' });
  };

  const helpWithSecondaryChange = (option: string): void => {
    if (option === state.helpWithSecondary) {
      return;
    }
    clearSecondSelectState();
  
    switch (state.helpWith) {
      case helpWithOptions.business:
        updateState({ isSerialNumberShown: true });
        props.onDetailsChange({ showDetails: true });
        break;
      case helpWithOptions.enterprise:
        updateState({ thirdDropdownOptions: helpWithThirdOptions });
        break;
    }

    updateState({ helpWithSecondary: option });
  };

  const helpWithThirdChange = (option: string): void => {
    clearThirdSelectState();

    updateState({ helpWithThird: option });
    props.onDetailsChange({ showDetails: true });
  };

  const clearSecondSelectState = (): void => {
    clearThirdSelectState();

    updateState({
      helpWithSecondary: '',
      thirdDropdownOptions: [],
      isSerialNumberShown: false
    });
    props.onDetailsChange({ showDetails: false });
  };

  const clearThirdSelectState = (): void => {
    updateState({ helpWithThird: '' });
    props.onDetailsChange({ showDetails: false });
  };

  const clearState = (): void => {
    clearSecondSelectState();
    updateState({
      helpWith: '',
      helpWithSecondary: '',
      helpWithThird: '',
      secondDropdownOptions: null,
      thirdDropdownOptions: []
    });
  };

  props.onReset && props.onReset(clearState);

  return (
    <>
      <HelpWith
        selectType={!props.isConsolidated ? HelpWithType.First : HelpWithType.Second}
        options={Object.values(helpWithOptions)}
        value={state.helpWith}
        onChange={helpWithChange}
      />

        {state.secondDropdownOptions &&
          <HelpWith
            selectType={!props.isConsolidated ? HelpWithType.Second : HelpWithType.Third}
            options={Object.values(state.secondDropdownOptions)}
            value={state.helpWithSecondary}
            onChange={helpWithSecondaryChange}
          />
        }

        {state.thirdDropdownOptions.length > 0 &&
          <HelpWith
            selectType={!props.isConsolidated ? HelpWithType.Third : HelpWithType.Fourth}
            options={Object.values(state.thirdDropdownOptions)}
            value={state.helpWithThird}
            onChange={helpWithThirdChange}
          />
        }

        <FormField name="severity" htmlFor="severity" label="Severity (business impact)" style={{ display: 'none' }} required>
          <Select id="severity" name="severity" placeholder="Select" value={severityOptions.medium} options={Object.values(severityOptions)} />
        </FormField>

        {state.isSerialNumberShown &&
          <FormField name="serialNumber" htmlFor="serial-number" label="Serial number" required>
            <TextInput id="serial-number" name="serialNumber" placeholder="Serial number"/>
          </FormField>
        }
    </>
  );
};
